import Decimal from 'decimal.js';
import { cn } from '@/lib/utils';
import { ehDecimal, formatarFracaoPercentual } from '@/lib/format';

export interface ProfitBadgeProps {
  /** Lucro como fracao do preco (string decimal vinda da API: "0.2184" = 21,84%). Ausente sem `lucro.ver` (so cor). */
  lucro?: string;
  /** Fracao ja arredondada para exibir (da API). So a exibicao usa; a faixa vem sempre de `lucro`. */
  lucroExibido?: string;
  /** Meta de lucro (fracao). Ausente sem `lucro.ver`. */
  meta?: string;
  /** Lucro minimo aceito (fracao). Ausente sem `lucro.ver`. */
  minimo?: string;
  /** Faixa ja decidida pelo motor ('green' | 'orange' | 'red'). Quando vem, prevalece sobre a comparacao local. */
  faixa?: 'green' | 'orange' | 'red';
  /** So o percentual, sem o rotulo (grades densas). O rotulo segue em `title` e `aria-label`. */
  compacto?: boolean;
  className?: string;
}

type Faixa = 'meta' | 'minimo' | 'abaixo';

/** Decimal so para COMPARAR e escolher a cor. Nada calculado vai para a tela. */
function faixaDe(lucro: string, meta: string, minimo: string): Faixa {
  const l = new Decimal(lucro);
  if (l.gte(meta)) return 'meta';
  if (l.gte(minimo)) return 'minimo';
  return 'abaixo';
}

const ESTILO: Record<Faixa, string> = {
  meta: 'bg-success-bg text-success-ink',
  minimo: 'bg-warning-bg text-warning-ink',
  abaixo: 'bg-danger-bg text-danger-ink',
};

const FAIXA_API_PARA_ESTILO = { green: 'meta', orange: 'minimo', red: 'abaixo' } as const;

/** Rotulos do modo so-cor (sem `lucro.ver`): sem numero, sem marca de meta ou minimo. */
export const ROTULO_FAIXA_SO_COR: Record<'green' | 'orange' | 'red', string> = { green: 'Na meta', orange: 'Abaixo da meta', red: 'Abaixo do mínimo' };

/** Cor cheia da faixa (barra ou pilula so-cor). Mesmos tokens do ESTILO. */
export const COR_FAIXA_SO_COR: Record<'green' | 'orange' | 'red', string> = { green: 'bg-success-bg text-success-ink', orange: 'bg-warning-bg text-warning-ink', red: 'bg-danger-bg text-danger-ink' };

/** Pill de lucro: verde >= meta, laranja >= minimo, vermelho < minimo (bordas inclusivas). */
export function ProfitBadge({ lucro, lucroExibido, meta, minimo, faixa: faixaApi, compacto = false, className }: ProfitBadgeProps) {
  // Sem `lucro.ver` a API omite lucro, meta e minimo: sobra so a faixa, mostrada como pilula de cor (sem numero nem marca).
  if (faixaApi && (lucro === undefined || meta === undefined || minimo === undefined)) {
    return (
      <span data-faixa={FAIXA_API_PARA_ESTILO[faixaApi]} data-testid="so-cor" className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-semibold', COR_FAIXA_SO_COR[faixaApi], className)}>
        {ROTULO_FAIXA_SO_COR[faixaApi]}
      </span>
    );
  }
  if (lucro === undefined || meta === undefined || minimo === undefined) {
    return (
      <span className={cn('rounded-full bg-secondary px-2.5 py-0.5 text-caption font-medium text-secondary-foreground', className)}>
        Lucro indisponível
      </span>
    );
  }
  if (!ehDecimal(lucro) || !ehDecimal(meta) || !ehDecimal(minimo)) {
    return (
      <span className={cn('rounded-full bg-secondary px-2.5 py-0.5 text-caption font-medium text-secondary-foreground', className)}>
        Lucro indisponível
      </span>
    );
  }

  const faixa: Faixa = faixaApi ? FAIXA_API_PARA_ESTILO[faixaApi] : faixaDe(lucro, meta, minimo);
  const rotulo =
    faixa === 'meta'
      ? new Decimal(lucro).lte(meta) // eq, ou abaixo por menos de meio centavo no preco (tolerancia da faixa decidida pelo motor): continua "Na meta"
        ? 'Na meta'
        : 'Acima da meta'
      : faixa === 'minimo'
        ? 'Abaixo da meta'
        : 'Abaixo do mínimo';

  const pct = formatarFracaoPercentual(lucroExibido ?? lucro);
  return (
    <span
      data-faixa={faixa}
      title={compacto ? rotulo : undefined}
      aria-label={compacto ? `${rotulo} · ${pct}` : undefined}
      className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-semibold', ESTILO[faixa], className)}
    >
      {compacto ? pct : `${rotulo} · ${pct}`}
    </span>
  );
}

const ROTULO_FAIXA: Record<Faixa, string> = { meta: 'Meta atingida', minimo: 'Abaixo da meta', abaixo: 'Abaixo do mínimo' };

export interface FaixaLucroBadgeProps {
  /** Faixa ja decidida pela API (`green | orange | red`). Sem lucro numerico: a tela nao tem o numero. */
  faixa: 'green' | 'orange' | 'red';
  className?: string;
}

/**
 * Mesma paleta do `ProfitBadge`, sem o percentual. Para quem nao recebe o lucro em numero (VENDEDOR), que so
 * ve a mudanca de cor entre o envio e hoje. Nao compara nada: a faixa vem da API.
 */
export function FaixaLucroBadge({ faixa, className }: FaixaLucroBadgeProps) {
  const estilo = FAIXA_API_PARA_ESTILO[faixa];
  return (
    <span
      data-faixa={estilo}
      data-testid="faixa-lucro"
      className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-semibold', ESTILO[estilo], className)}
    >
      {ROTULO_FAIXA[estilo]}
    </span>
  );
}
